import styled from '@emotion/styled';
import { useWatch } from 'react-hook-form';
import { Form, FormField, Grid, Segment } from 'semantic-ui-react';
import { TitleSegment } from '@britishcouncil/react-admin';
import { RHFormSelect, useFormContext } from 'components';

type NextMonitoringDateFormData = {
  nextMonitoringDateType: 'system' | 'manual';
  customNextMonitoringDate: number | null;
};

export const NextMonitoringDateForm = ({
  nextDateOptions,
}: {
  nextDateOptions: Array<{
    id: number;
    name: string;
    calculated: boolean;
  }>;
}) => {
  const {
    mode,
    formMethods: { control, setValue },
  } = useFormContext<NextMonitoringDateFormData>();
  const systemNextMonitoringDate = nextDateOptions?.find((x) => x.calculated)?.name ?? '';

  const nextMonitoringDateType = useWatch({
    control,
    name: 'nextMonitoringDateType',
  });

  const handleNextMonitoringDateTypeChange = (type: 'system' | 'manual') => {
    setValue('nextMonitoringDateType', type, {
      shouldValidate: true,
    });
    if (type === 'system') {
      setValue('customNextMonitoringDate', null, {
        shouldValidate: true,
      });
    }
  };

  return (
    <>
      <TitleSegment title="Next Monitoring Date" />
      <Segment>
        <Grid columns={4} stackable>
          <Grid.Column width={8}>
            <FormField>
              <S.Label>Calculated</S.Label>
              <S.CheckboxWrapper>
                <Form.Radio
                  label={systemNextMonitoringDate}
                  value="system"
                  readOnly={mode === 'read'}
                  checked={nextMonitoringDateType === 'system'}
                  onChange={() => handleNextMonitoringDateTypeChange('system')}
                />
              </S.CheckboxWrapper>
            </FormField>
          </Grid.Column>
          <Grid.Column width={8}>
            <FormField>
              <S.Label>Set manually</S.Label>
              <S.ManualSection>
                <S.CheckboxWrapper>
                  <Form.Radio
                    label=""
                    value="manual"
                    checked={nextMonitoringDateType === 'manual'}
                    readOnly={mode === 'read'}
                    onChange={() => handleNextMonitoringDateTypeChange('manual')}
                  />
                </S.CheckboxWrapper>
                <S.DropdownWrapper>
                  <RHFormSelect
                    name={'customNextMonitoringDate'}
                    placeholder="None selected"
                    control={control}
                    options={nextDateOptions
                      .filter((x) => !x.calculated)
                      .map((x) => ({ text: x.name, value: x.id }))}
                    disabled={nextMonitoringDateType === 'system'}
                  />
                </S.DropdownWrapper>
              </S.ManualSection>
            </FormField>
          </Grid.Column>
        </Grid>
      </Segment>
    </>
  );
};

const S = {
  Label: styled.span`
    display: block;
    margin: 0 0 0.3rem 0;
    color: rgba(0, 0, 0, 0.9);
    font-size: 0.9em;
    font-weight: 700;
  `,
  CheckboxWrapper: styled.div`
    margin-top: 8px;
  `,
  ManualSection: styled.div`
    display: flex;
    align-items: top;
    justify-content: space-between;
    && > .field {
      margin: 0;
    }
  `,
  DropdownWrapper: styled.div`
    flex-grow: 1;
  `,
};
